import { useForm, usePage } from "@inertiajs/react";
import { useState } from "react";
import { FiArrowRight } from "react-icons/fi";

import { formatCEP, formatCPF, formatPhone } from "@/utils/cpfValidator";

import PillInput from "./components/PillInput";
import PillSelect from "./components/PillSelect";

const UTILITY_OPTIONS = ["COPEL", "Outra"];

interface LeadFormData extends Record<string, string> {
  name: string;
  email: string;
  phone: string;
  cpf: string;
  utility_company: string;
  cep: string;
  average_energy_consumption: string;
}

interface PageProps extends Record<string, unknown> {
  errors?: Record<string, string[]>;
}

export default function ParanaClubePage() {
  const { props } = usePage<PageProps>();
  const { errors } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clearedErrors, setClearedErrors] = useState<Set<string>>(new Set());

  const { data, setData, post } = useForm<LeadFormData>({
    name: "",
    email: "",
    phone: "",
    cpf: "",
    utility_company: "COPEL",
    cep: "",
    average_energy_consumption: "",
  });

  const clearError = (field: string) => {
    setClearedErrors((prev) => new Set(prev).add(field));
  };

  const hasError = (field: string): boolean => {
    if (clearedErrors.has(field)) return false;
    return !!errors?.[field];
  };

  const isFormValid = () => {
    return (
      data.name.trim() !== "" &&
      data.email.trim() !== "" &&
      data.phone.trim() !== "" &&
      data.cpf.trim() !== "" &&
      data.utility_company.trim() !== "" &&
      data.average_energy_consumption.trim() !== ""
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    setClearedErrors(new Set());

    post("/parana-clube", {
      data: {
        lead: data,
      },
      onFinish: () => {
        setIsSubmitting(false);
      },
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setData("phone", formatted);
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setData("cpf", formatted);
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCEP(e.target.value);
    setData("cep", formatted);
  };

  const handleConsumptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove non-numeric characters except digits
    value = value.replace(/\D/g, "");

    // Parse as integer and limit to 100000
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      const limitedValue = Math.min(numValue, 100_000);
      setData("average_energy_consumption", limitedValue.toString());
    } else {
      setData("average_energy_consumption", "");
    }
  };

  const getErrorMessage = (field: string): string | undefined => {
    if (errors?.[field]) {
      return Array.isArray(errors[field]) ? errors[field][0] : errors[field];
    }
    return undefined;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#d3d8db] p-[8px]">
      <div className="flex w-full flex-col items-stretch gap-[8px] sm:flex-row">
        {/* Hero section */}
        <div className="relative flex h-52 items-center justify-center overflow-hidden rounded-[32px] pr-[32px] sm:h-auto sm:w-1/2">
          {/* Background image */}
          <img
            style={{ background: "repeating-conic-gradient(#000 0 0.0001%,#0000 0 0.0002%)" }}
            src="/images/jogador-parana-noise.png"
            alt=""
            className="absolute inset-0 size-full object-cover"
          />
          {/* Logo and branding */}
          <div className="h-54 relative z-10 flex items-center gap-[17.3px]">
            <img src="/images/geedee-by-brp-white.png" alt="Geedee" className="sm:h-54 h-12" />
          </div>
        </div>

        {/* Form card */}
        <div className="flex flex-1 flex-col items-center rounded-[32px] bg-white p-6 sm:p-[56px]">
          <div className="w-full max-w-[496px]">
            {/* Header text */}
            <div className="mb-[32px]">
              <h1 className="mb-[8px] text-[40px] font-normal leading-[1.2] text-[#2a2f33]">
                Vamos calcular seu desconto exclusivo!
              </h1>
              <p className="text-[16px] font-normal leading-[1.6] text-[#818a8f]">
                Preencha seus dados abaixo para analisarmos sua elegibilidade e estimarmos sua
                economia mensal.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-[24px]">
              <PillInput
                label="Nome"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                error={hasError("name")}
                errorMessage={getErrorMessage("name")}
                onClearError={() => clearError("name")}
              />

              <PillInput
                label="Email"
                type="email"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                error={hasError("email")}
                errorMessage={getErrorMessage("email")}
                onClearError={() => clearError("email")}
              />

              <PillInput
                label="Celular"
                value={data.phone}
                onChange={handlePhoneChange}
                error={hasError("phone")}
                errorMessage={getErrorMessage("phone")}
                onClearError={() => clearError("phone")}
              />

              <PillSelect
                label="Concessionária"
                value={data.utility_company}
                onChange={(value) => setData("utility_company", value)}
                error={hasError("utility_company")}
                errorMessage={getErrorMessage("utility_company")}
                onClearError={() => clearError("utility_company")}
                options={UTILITY_OPTIONS}
              />

              <PillInput
                label="CPF"
                value={data.cpf}
                onChange={handleCpfChange}
                error={hasError("cpf")}
                errorMessage={getErrorMessage("cpf")}
                onClearError={() => clearError("cpf")}
                maxLength={14}
              />

              <PillInput
                label="CEP"
                value={data.cep}
                onChange={handleCepChange}
                error={hasError("cep")}
                errorMessage={getErrorMessage("cep")}
                onClearError={() => clearError("cep")}
                maxLength={9}
              />

              <PillInput
                label="Consumo médio de energia"
                type="number"
                value={data.average_energy_consumption}
                onChange={handleConsumptionChange}
                error={hasError("average_energy_consumption")}
                errorMessage={getErrorMessage("average_energy_consumption")}
                onClearError={() => clearError("average_energy_consumption")}
                suffix="kWh"
              />

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex h-[56px] w-[256px] items-center justify-between rounded-[56px] px-[24px] py-[16px] pr-[16px] transition-all ${
                  isFormValid() ? "bg-[#1c4189] hover:bg-[#163570]" : "bg-[#d3d8db]"
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <span
                  className={`font-['Inter'] text-[16px] font-bold leading-[1.6] ${isFormValid() ? "text-white" : "text-[#818a8f]"}`}
                >
                  {isSubmitting ? "Enviando..." : "Enviar"}
                </span>
                <div
                  className={`flex size-[24px] items-center justify-center rounded-[32px] ${isFormValid() ? "bg-white" : "bg-[#b2bcc2]"}`}
                >
                  <FiArrowRight
                    className={isFormValid() ? "text-[#1c4189]" : "text-[#818a8f]"}
                    size={14}
                  />
                </div>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
